import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { doubleTapProps } from '@/shared/device/doubleTap';
import { Link, useLocation } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { signOut } from '@/shared/auth/passwordAuth';
import { Icon, type IconName } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { useEditor } from '@/shared/editing/EditorContext';
import {
  getAllowedViewModes,
  getViewModeLabel,
  isViewModeActive,
  useOrg,
  type ViewAsMember,
} from '@/shared/org';
import { useUser } from '@/shared/auth';
import { supabase } from '@/lib/supabase';
import { ThemeSwitch } from '@/shared/theme';
import { foldersApi } from '@/shared/folders';
import { ShareButton } from '@/shared/sharing';
import { setTopBarMenuHosted, setTopBarMenuSlot } from '@/shared/components/topBarMenuSlot';
import { usePwaInstall } from '@/shared/pwa/usePwaInstall';
import { getContrastTextColor } from '@/shared/components/ui/Avatar';
import { HistorySplitButton } from '@/shared/components/Grid/HistorySplitButton';

/** The org's own name for a company; the word "Personal" for a personal one —
 *  a personal organization is typically named after its owner, and printing
 *  that name again right under the username it already sits beneath is just
 *  an echo. */
function orgContextLabel(organization: { kind: string; name: string }, t: ReturnType<typeof useI18n>['t']): string {
  return organization.kind === 'company' ? organization.name : t('org.switcher.personal');
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'U';
}

interface TopBarProps {
  documentLayout?: boolean;
}

export function TopBar({ documentLayout = false }: TopBarProps = {}) {
  const { t } = useI18n();
  const brand = useBrand();
  const user = useUser();
  const { canInstall, install } = usePwaInstall();
  const org = useOrg();
  const editor = useEditor();
  const location = useLocation();
  const [open, setOpen] = useState<'settings' | 'user' | 'more' | null>(null);
  const [viewUsers, setViewUsers] = useState<ViewAsMember[]>([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(false);
  const [documentName, setDocumentName] = useState<string | null>(null);
  const [sheetIsPublic, setSheetIsPublic] = useState(false);
  const [editingDocumentName, setEditingDocumentName] = useState<string | null>(null);
  const cancelDocumentEditRef = useRef(false);
  const recordedOpen = useRef<string | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const settingsMenu = useRef<HTMLDivElement>(null);
  const userMenu = useRef<HTMLDivElement>(null);
  const settingsTrigger = useRef<HTMLButtonElement>(null);
  const userTrigger = useRef<HTMLButtonElement>(null);
  const moreMenu = useRef<HTMLDivElement>(null);
  const moreTrigger = useRef<HTMLButtonElement>(null);
  const previousOpen = useRef<typeof open>(null);
  const name = typeof user?.user_metadata?.full_name === 'string' && user.user_metadata.full_name.trim() ? user.user_metadata.full_name : user?.email ?? t('topbar.user');
  const avatarColor = typeof user?.user_metadata?.avatar_color === 'string' && user.user_metadata.avatar_color.trim() ? user.user_metadata.avatar_color.trim() : null;

  useEffect(() => {
    const close = (event: PointerEvent) => { if (!wrapper.current?.contains(event.target as Node)) setOpen(null); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(null); };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('pointerdown', close); window.removeEventListener('keydown', escape); };
  }, []);
  useEffect(() => { setOpen(null); }, [location.pathname, location.search]);
  // Phones: documents get one ⋮ menu here, and the grid's toolbar overflow moves into it.
  useEffect(() => {
    setTopBarMenuHosted(documentLayout);
    return () => setTopBarMenuHosted(false);
  }, [documentLayout]);

  // Trip Board lives at /trips/board/:id (see editorPathFor), one segment
  // deeper than every other document type — skip an optional 'board/' so it
  // still resolves to the right documentId instead of capturing 'board'.
  const documentMatch = location.pathname.match(/^\/(sheets|trips|investments|notes|forms|cashflow)\/(?:board\/)?([^/]+)/);
  const documentType = documentMatch?.[1] ?? null;
  const documentId = documentMatch?.[2] ?? null;
  const isSheetDocument = documentType === 'sheets';

  useEffect(() => {
    setEditingDocumentName(null);
    if (!documentId) { setDocumentName(null); setSheetIsPublic(false); return; }
    const documentPromise = isSheetDocument ? foldersApi.getSheetDocument(documentId) : Promise.resolve(null);
    void Promise.all([foldersApi.getNode(documentId), documentPromise])
      .then(([node, document]) => {
        setDocumentName(node?.name ?? null);
        setSheetIsPublic(document?.isPublic ?? false);
        // The one place every document type passes through, which is why the
        // open history is recorded here instead of in three editors that would
        // drift. A non-null node means RLS already approved the read, and the
        // ref keeps a re-render of this effect from counting a second visit.
        if (node && recordedOpen.current !== node.id) {
          recordedOpen.current = node.id;
          void foldersApi.recordNodeOpen(node.id);
        }
      })
      .catch(() => { setDocumentName(null); setSheetIsPublic(false); });
  }, [documentId, isSheetDocument, org.status, user?.id]);

  useEffect(() => {
    const onPublicChange = (event: Event) => {
      const custom = event as CustomEvent<{ id: string; isPublic: boolean }>;
      if (isSheetDocument && custom.detail?.id === documentId) setSheetIsPublic(custom.detail.isPublic);
    };
    window.addEventListener('sheet:public-changed', onPublicChange);
    return () => window.removeEventListener('sheet:public-changed', onPublicChange);
  }, [documentId, isSheetDocument]);

  useEffect(() => {
    const onRename = (event: Event) => {
      const custom = event as CustomEvent<{ id: string; name: string }>;
      if (custom.detail && custom.detail.id === documentId) {
        setDocumentName(custom.detail.name);
      }
    };
    window.addEventListener('folders:rename-node', onRename);
    return () => window.removeEventListener('folders:rename-node', onRename);
  }, [documentId]);

  useEffect(() => {
    if (documentName) {
      document.title = `${documentName} · ${brand.name}`;
    }
  }, [documentName, brand.name]);
  useEffect(() => {
    if (!open) return;
    const menu = open === 'settings' ? settingsMenu.current : open === 'more' ? moreMenu.current : userMenu.current;
    menu?.querySelector<HTMLElement>('[role="menuitem"], button:not([disabled])')?.focus();
  }, [open]);
  useEffect(() => {
    const previous = previousOpen.current;
    if (previous && !open) {
      (previous === 'settings' ? settingsTrigger.current : previous === 'more' ? moreTrigger.current : userTrigger.current)?.focus();
    }
    previousOpen.current = open;
  }, [open]);

  const menuKeyboard = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"], button:not([disabled])'));
    if (!items.length) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Escape') { event.preventDefault(); setOpen(null); return; }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  };

  const showStatus = editor.saveState !== 'clean' || Boolean(editor.customStatus);
  const statusLabel = editor.saveState === 'clean' && editor.customStatus ? editor.customStatus : t(`form.save.${editor.saveState}`);
  const canViewAs = org.status === 'ready' && (org.actualRole === 'owner' || org.actualRole === 'developer');
  const readOnly = org.status === 'ready' && Boolean(org.isViewingAs);
  const showHistoryControls = editor.showTopBarHistory && (editor.canUndo || editor.canRedo || Boolean(editor.undoTasks));

  const saveDocumentName = async () => {
    if (editingDocumentName === null || !documentId || readOnly) return;
    const trimmed = editingDocumentName.trim();
    setEditingDocumentName(null);
    if (!trimmed || trimmed === documentName) return;
    editor.setSaveState('unsaved');
    try {
      await foldersApi.renameNode(documentId, trimmed);
      setDocumentName(trimmed);
      editor.setSaveState('saved');
    } catch {
      editor.setSaveState('failed');
    }
  };
  useEffect(() => {
    if (open !== 'user' || !canViewAs || !user) return;
    setViewLoading(true);
    setViewError(false);
    void (async () => {
      const { data } = await supabase.auth.getSession();
      const headers: Record<string, string> = data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {};
      if (org.status === 'ready') headers['X-Active-Org'] = org.orgId;
      const response = await fetch('/api/view-as/users', { headers });
      if (response.ok) {
        const body = await response.json() as { users: ViewAsMember[] };
        setViewUsers(body.users);
      } else {
        setViewUsers([]);
        setViewError(true);
      }
    })().catch(() => { setViewUsers([]); setViewError(true); }).finally(() => setViewLoading(false));
  }, [canViewAs, open, org, user]);
  const documentControls = <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
    {documentName && (
      <div className="flex min-w-0 shrink flex-col justify-center sm:-ml-1.5 sm:flex-row sm:items-center sm:border-l sm:border-header-muted/40 sm:pl-2">
        {editingDocumentName !== null ? (
          <input
            type="text"
            value={editingDocumentName}
            onChange={(e) => setEditingDocumentName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelDocumentEditRef.current = true;
                setEditingDocumentName(null);
              }
            }}
            onBlur={() => {
              if (cancelDocumentEditRef.current) {
                cancelDocumentEditRef.current = false;
                return;
              }
              void saveDocumentName();
            }}
            autoFocus
            onFocus={(e) => e.target.select()}
            aria-label={t('sheet.renamePrompt')}
            className="h-8 w-32 min-w-0 max-w-[10rem] rounded-full border border-border-strong bg-surface px-2 text-lg text-fg focus:outline-none focus:ring-1 focus:ring-border-strong truncate sm:h-7 sm:w-44 sm:max-w-[14rem] sm:text-sm"
          />
        ) : (
          <>
            <span className="flex min-w-0 items-center gap-1">
              <span
                role={readOnly ? undefined : 'button'}
                tabIndex={readOnly ? undefined : 0}
                {...doubleTapProps(() => {
                  if (!readOnly && documentName) {
                    setEditingDocumentName(documentName);
                  }
                })}
                onKeyDown={(e) => {
                  if (!readOnly && (e.key === 'Enter' || e.key === ' ') && documentName) {
                    e.preventDefault();
                    setEditingDocumentName(documentName);
                  }
                }}
                title={readOnly ? documentName : `${documentName} — ${t('sheet.renamePrompt')}`}
                className={`min-w-0 max-w-[10rem] shrink truncate rounded px-1.5 py-0.5 text-lg font-medium text-header-fg sm:max-w-[14rem] sm:rounded-full sm:px-1.5 sm:py-1 sm:text-sm sm:font-normal ${
                  readOnly ? '' : 'cursor-pointer hover:bg-header-hover transition-colors'
                }`}
              >
                {documentName}
              </span>
              {sheetIsPublic && <span role="img" aria-label={t('sheet.public.indicatorLabel')} title={t('sheet.public.indicatorLabel')} className="shrink-0 text-base sm:text-sm">🌐</span>}
            </span>
            {showStatus && (
              <span
                role="status"
                aria-live="polite"
                title={statusLabel}
                className={`block max-w-[10rem] truncate px-1 text-[0.625rem] leading-tight sm:hidden ${
                  editor.saveState === 'unsaved' || editor.saveState === 'failed'
                    ? 'text-danger font-medium'
                    : 'text-header-muted'
                }`}
              >
                {statusLabel}
              </span>
            )}
          </>
        )}
      </div>
    )}
    {editor.topBarRefresh && (
      <button
        type="button"
        onClick={editor.topBarRefresh.run}
        disabled={editor.topBarRefresh.busy}
        aria-busy={editor.topBarRefresh.busy}
        aria-label={t('grid.refresh')}
        title={editor.topBarRefresh.title}
        className={`${showHistoryControls ? '' : 'mr-2 '}ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-50 sm:hidden`}
      >
        <svg aria-hidden="true" className={editor.topBarRefresh.busy ? 'animate-spin' : ''} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
          <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M3 21v-5h5" />
        </svg>
      </button>
    )}
    {showHistoryControls && (
      <div className={`${editor.topBarRefresh ? 'ml-0 sm:ml-0' : 'ml-auto sm:ml-0'} mr-2 flex items-center gap-1 sm:mr-0`}>
        <HistorySplitButton
          direction="undo"
          tasks={editor.undoTasks ?? []}
          onRun={editor.undoMany ?? (() => editor.undo())}
          disabled={!editor.canUndo}
        />
        {(editor.canRedo || (editor.redoTasks && editor.redoTasks.length > 0)) && (
          <HistorySplitButton
            direction="redo"
            tasks={editor.redoTasks ?? []}
            onRun={editor.redoMany ?? (() => editor.redo())}
            disabled={!editor.canRedo}
          />
        )}
      </div>
    )}
    {org.status === 'ready' && org.isViewingAs ? <button type="button" onClick={org.stopViewingAs} className="inline max-w-28 shrink-0 truncate rounded-full bg-accent-soft px-2 py-1 text-[0.625rem] text-accent sm:max-w-48 sm:text-xs" title={t('topbar.viewAs.stop')}>{t('topbar.viewAs.active', { name: org.viewAs?.name ?? org.viewAs?.email ?? t('topbar.user') })}</button> : null}
    {showStatus && (
      <span
        role="status"
        aria-live="polite"
        title={statusLabel}
        className={`${
          documentName ? 'hidden sm:inline' : 'inline'
        } max-w-[6rem] shrink-0 truncate whitespace-nowrap rounded-full px-2 py-1 text-[0.625rem] sm:max-w-[20rem] sm:px-3 sm:text-xs ${
          editor.saveState === 'unsaved' || editor.saveState === 'failed'
            ? 'bg-danger-soft text-danger'
            : 'bg-header-hover text-header-muted'
        }`}
      >
        {statusLabel}
      </span>
    )}
    {editor.shareConfig && (
      <div className={documentLayout ? 'sm:ml-auto' : 'ml-auto'}>
        <ShareButton
          variant="surface"
          className="!bg-brand-soft !text-brand !hover:bg-brand-mid !border-transparent"
          subject={editor.shareConfig.subject}
          adapter={editor.shareConfig.adapter}
        />
      </div>
    )}
  </div>;

  const accountControls = <div className={`ml-auto flex min-w-0 items-center ${documentLayout ? 'gap-0' : 'gap-1'} sm:gap-2`}>
    <div className="relative flex items-center gap-1">
      {canInstall && <button type="button" onClick={() => void install()} aria-label={t('common.install')} title={t('common.install')} className="tap-target hidden items-center justify-center rounded-full text-header-muted hover:bg-header-hover hover:text-header-fg sm:flex"><Icon name="install" /></button>}
      {/* Phones: Home's apps shortcut lives here, its toolbar row is too narrow. */}
      {(location.pathname === '/' || location.pathname.startsWith('/folders')) && (
        <Link to="/apps" aria-label={t('home.allApps')} title={t('home.allApps')} className="tap-target flex items-center justify-center rounded-full text-header-muted hover:bg-header-hover hover:text-header-fg sm:hidden"><Icon name="apps" filled /></Link>
      )}
      <Link
        to="/ai"
        aria-label={t('topbar.mcp')}
        title={t('topbar.mcp')}
        className="tap-target hidden items-center justify-center rounded-full text-header-muted hover:bg-header-hover hover:text-header-fg px-2 text-xs font-semibold sm:flex"
      >
        <span className="flex items-center gap-1">
          <Icon name="bot" className="h-4 w-4" />
          <span className="hidden md:inline">{t('topbar.mcp')}</span>
        </span>
      </Link>
      <Link
        to="/help"
        aria-label={t('help.title')}
        title={t('help.title')}
        className={`tap-target ${documentLayout ? 'hidden sm:flex' : 'flex'} items-center justify-center rounded-full text-header-muted hover:bg-header-hover hover:text-header-fg`}
      >
        <Icon name="help" />
      </Link>
      <button ref={settingsTrigger} type="button" onClick={() => setOpen(open === 'settings' ? null : 'settings')} aria-haspopup="menu" aria-expanded={open === 'settings'} aria-label={t('topbar.settings')} title={t('topbar.settings')} className={`tap-target ${documentLayout ? 'hidden sm:flex' : 'flex'} items-center justify-center rounded-full text-header-muted hover:bg-header-hover hover:text-header-fg`}><Icon name="settings" /></button>
      {open === 'settings' && (
        <div
          ref={settingsMenu}
          role="menu"
          aria-label={t('topbar.settings')}
          onKeyDown={menuKeyboard}
          className="absolute right-0 top-full z-50 mt-1.5 max-h-[calc(100dvh-var(--header-h,3.5rem)-1rem)] w-72 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl border border-border bg-surface p-2 shadow-xl"
        >
          <ThemeModeMenu />
          <div className="py-1">
            <MenuLink icon="bot" to="/ai" label={t('topbar.mcp')} />
            <MenuLink icon="settings" to="/settings" label={t('topbar.settings')} />
            <MenuLink icon="building" to="/settings?s=organization" label={t('settings.section.organization.title')} />
            <MenuLink icon="user" to="/settings?s=preferences" label={t('settings.section.preferences.title')} />
            <MenuLink icon="calculators" to="/settings?s=formats" label={t('settings.section.formats.title')} />
            <MenuLink icon="palette" to="/settings?s=theme-colors" label={t('settings.section.themeColors.title')} />
            <MenuLink icon="alignment" to="/settings?s=grid-alignment" label={t('settings.section.gridAlignment.title')} />
            <MenuLink icon="investments" to="/admin?tab=plan" label={t('settings.section.plan.title')} />
            <MenuLink icon="userPlus" to="/admin" label={t('topbar.users')} />
            <MenuLink icon="notes" to="/forms" label={t('modules.forms.name')} />
          </div>
        </div>
      )}
    </div>
    <div className="relative">
      <button ref={userTrigger} type="button" onClick={() => setOpen(open === 'user' ? null : 'user')} aria-haspopup="menu" aria-expanded={open === 'user'} aria-label={name} className={`tap-target ${documentLayout ? 'hidden sm:flex' : 'flex'} min-w-11 shrink-0 items-center justify-center gap-1 rounded-full px-1 text-left hover:bg-header-hover sm:gap-2`}>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            avatarColor ? '' : 'bg-accent text-accent-fg'
          }`}
          style={
            avatarColor
              ? { backgroundColor: avatarColor, color: getContrastTextColor(avatarColor) }
              : undefined
          }
        >
          {initials(name)}
        </span>
        <div className="hidden sm:flex flex-col min-w-0 max-w-[6rem] md:max-w-[9rem] xl:max-w-[12rem] text-left leading-tight">
          <span className="truncate text-xs text-header-fg sm:text-sm">{name}</span>
          {org.status === 'ready' && (
            <span className="truncate text-[0.625rem] text-header-muted">
              {orgContextLabel(org.membership.organization, t)} · {getViewModeLabel(org.role, t)}
            </span>
          )}
        </div>
        <Icon name="chevronDown" className="hidden h-4 w-4 shrink-0 text-header-muted xl:block" />
      </button>
      {open === 'user' && (
        <div
          ref={userMenu}
          role="menu"
          aria-label={name}
          onKeyDown={menuKeyboard}
          className="absolute right-0 top-full z-50 mt-1.5 max-h-[calc(100dvh-var(--header-h,3.5rem)-1rem)] w-72 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl border border-border bg-surface p-3 shadow-xl"
        >
          <div className="border-b border-border pb-2.5">
            <p className="truncate text-sm font-medium text-fg">{name}</p>
            <p className="truncate text-xs text-muted">{user?.email ?? ''}</p>
            {!(org.status === 'ready' && org.memberships.length > 1) && (
              <p className="mt-1 truncate text-xs text-muted">
                {org.status === 'ready' ? `${orgContextLabel(org.membership.organization, t)} · ${getViewModeLabel(org.role, t)}` : ''}
              </p>
            )}
          </div>
          {org.status === 'ready' && org.memberships.length > 1 && (
            <div className="mt-2 border-b border-border pb-2">
              <p className="px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-muted">
                {t('org.switcher.label')}
              </p>
              <div className="space-y-0.5" role="radiogroup" aria-label={t('org.switcher.label')}>
                {org.memberships.map((m) => {
                  const isActive = m.orgId === org.orgId;
                  return (
                    <button
                      key={m.orgId}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => org.setActiveOrg(m.orgId)}
                      className={`flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-left text-sm transition-colors ${
                        isActive ? 'bg-surface-3 font-medium text-fg' : 'text-fg hover:bg-surface-2'
                      }`}
                    >
                      <Icon name={m.organization.kind === 'company' ? 'building' : 'user'} className="h-4 w-4 shrink-0 text-muted" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{m.organization.name}</span>
                        <span className="block truncate text-xs text-muted">
                          {t(m.organization.kind === 'company' ? 'org.switcher.company' : 'org.switcher.personal')}
                          {' · '}
                          {t(`users.role.${m.role}`)}
                        </span>
                      </span>
                      {isActive && <Icon name="check" className="h-4 w-4 shrink-0 text-accent" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <Link role="menuitem" to="/settings?s=preferences" onClick={() => setOpen(null)} className="mt-2 flex min-h-11 items-center gap-2 rounded-xl px-2.5 text-sm text-fg hover:bg-surface-3 transition-colors">
            <Icon name="user" className="h-4 w-4 shrink-0 text-muted" />
            {t('topbar.profile')}
          </Link>
          {org.status === 'ready' && (
            <div className="mt-2 border-t border-border pt-2">
              <p className="px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-muted">
                {t('topbar.viewMode')}
              </p>
              <div className="space-y-0.5" role="radiogroup" aria-label={t('topbar.viewMode')}>
                {getAllowedViewModes(org.actualRole).map((mode) => {
                  const isActive = isViewModeActive(mode, org.activeViewMode, org.actualRole);
                  return (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => {
                        org.setViewMode(mode);
                      }}
                      className={`flex min-h-9 w-full items-center justify-between rounded-xl px-2.5 py-1.5 text-left text-sm transition-colors ${
                        isActive
                          ? 'bg-surface-3 font-medium text-fg'
                          : 'text-muted hover:bg-surface-2 hover:text-fg'
                      }`}
                    >
                      <span className="truncate">{getViewModeLabel(mode, t)}</span>
                      {isActive && (
                        <Icon name="check" className="h-4 w-4 shrink-0 text-accent" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {canViewAs ? (
            <div className="mt-2 border-t border-border pt-2">
              <p className="px-2.5 py-1 text-xs font-medium text-muted">{t('topbar.viewAs.title')}</p>
              {org.status === 'ready' && org.isViewingAs ? (
                <button type="button" onClick={() => { org.stopViewingAs(); setOpen(null); }} className="flex min-h-11 w-full items-center rounded-xl px-2.5 text-left text-sm text-accent hover:bg-accent-soft transition-colors">
                  {t('topbar.viewAs.stop')}
                </button>
              ) : null}
              {viewLoading ? (
                <p role="status" className="px-2.5 py-2 text-xs text-muted">{t('common.loading')}</p>
              ) : viewError ? (
                <p role="alert" className="px-2.5 py-2 text-xs text-danger">{t('topbar.viewAs.error')}</p>
              ) : (
                viewUsers.filter((member) => member.userId !== user?.id).map((member) => (
                  <button key={member.userId} type="button" onClick={() => { if (org.status === 'ready') void org.startViewingAs(member).then((started) => { if (started) setOpen(null); else setViewError(true); }); }} className="flex min-h-11 w-full flex-col items-start rounded-xl px-2.5 py-1.5 text-left text-sm text-fg hover:bg-surface-3 transition-colors">
                    <span className="truncate w-full">{member.name ?? member.email ?? t('topbar.user')}</span>
                    <span className="text-xs text-muted">{t(`users.role.${member.role}`)}</span>
                  </button>
                ))
              )}
            </div>
          ) : null}
          <button role="menuitem" type="button" onClick={() => void signOut()} className="mt-1 flex min-h-11 w-full items-center gap-2 rounded-xl px-2.5 text-left text-sm text-danger hover:bg-danger-soft transition-colors">
            <Icon name="close" className="h-4 w-4 shrink-0" />
            {t('auth.session.signOut')}
          </button>
        </div>
      )}
    </div>
    {documentLayout && (
      <div className="relative sm:hidden">
        <button ref={moreTrigger} type="button" onClick={() => setOpen(open === 'more' ? null : 'more')} aria-haspopup="menu" aria-expanded={open === 'more'} aria-label={t('grid.moreOptions')} title={t('grid.moreOptions')} className="flex h-11 w-6 items-center justify-center rounded-full text-header-muted hover:bg-header-hover hover:text-header-fg"><svg aria-hidden="true" viewBox="0 0 4 16" className="h-4 w-1 fill-current"><circle cx="2" cy="2" r="1.8" /><circle cx="2" cy="8" r="1.8" /><circle cx="2" cy="14" r="1.8" /></svg></button>
        {open === 'more' && (
          <div
            ref={moreMenu}
            role="menu"
            aria-label={t('grid.moreOptions')}
            onKeyDown={menuKeyboard}
            className="absolute right-0 top-full z-50 mt-1.5 [&:has([data-submenu-open])_*:not([data-submenu-open]):not([data-submenu-open]_*):not(:has([data-submenu-open]))]:hidden max-h-[calc(100dvh-var(--header-h,3.5rem)-1rem)] w-72 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl border border-border bg-surface py-1 shadow-xl"
          >
            <div ref={(el) => setTopBarMenuSlot(el, () => setOpen(null))} className="empty:hidden" />
            <MenuLink icon="help" to="/help" label={t('help.title')} />
            <MenuButton icon="settings" label={t('topbar.settings')} onClick={() => setOpen('settings')} />
            <MenuButton icon="user" label={t('topbar.profile')} onClick={() => setOpen('user')} />
          </div>
        )}
      </div>
    )}
  </div>;

  return (
    <div ref={wrapper} className="flex min-w-0 max-w-full flex-1 items-center gap-1 sm:gap-2">
      {documentControls}
      {accountControls}
    </div>
  );
}

function MenuLink({ icon, to, label }: { icon: IconName; to: string; label: string }) {
  return (
    <Link role="menuitem" to={to} className="flex min-h-10 items-center gap-3 rounded-xl px-3 py-2 text-sm text-fg hover:bg-surface-3 transition-colors">
      <Icon name={icon} className="h-4 w-4 shrink-0 text-muted" />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function MenuButton({ icon, label, onClick }: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button role="menuitem" type="button" onClick={onClick} className="flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-sm text-fg hover:bg-surface-3 transition-colors">
      <Icon name={icon} className="h-4 w-4 shrink-0 text-muted" />
      <span className="truncate">{label}</span>
    </button>
  );
}

function ThemeModeMenu() {
  const { t } = useI18n();
  return (
    <div className="mb-1 border-b border-border px-1 pb-2.5 pt-1">
      <div className="mb-1.5 flex items-center gap-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted">
        <Icon name="sun" className="h-3.5 w-3.5 shrink-0" />
        <span>{t('settings.theme.mode')}</span>
      </div>
      <ThemeSwitch />
    </div>
  );
}
